import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Radar, SearchCheck, Zap } from "lucide-react";
import { scams } from "../data/scams";
import { Header } from "../components/Header";
import { Marquee } from "../components/Marquee";
import { Footer } from "../components/Footer";
import { Mascot } from "../components/Mascot";
import { ScamCard } from "../components/ScamCard";
import { FeedTheLizard } from "../components/FeedTheLizard";

const revealLine = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const steps = [
  {
    n: "01",
    icon: Radar,
    title: "WE FIND IT",
    body: "We watch for money-making offers spreading through social media.",
  },
  {
    n: "02",
    icon: SearchCheck,
    title: "WE INVESTIGATE IT",
    body: "We examine the claims, costs, pressure tactics, complaint trails and supporting evidence.",
  },
  {
    n: "03",
    icon: Zap,
    title: "YOU GET THE SCOOP",
    body: "Dream. Goal. Catch. Risk. Receipts. Then get on with your life.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const mascotY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const mascotRotate = useTransform(scrollYProgress, [0, 1], [0, 18]);

  return (
    <div className="min-h-screen">
      <Header />

      <section ref={heroRef} className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#EBF7EE] blur-3xl opacity-80" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-9 sm:pt-14 sm:pb-12">
          <div className="flex items-end justify-between gap-6">
            <h1 className="font-display font-bold tracking-tight text-[#141915] leading-[1.06] text-[2.1rem] sm:text-5xl lg:text-6xl">
              <span className="block overflow-hidden pb-1">
                <motion.span custom={0} variants={revealLine} initial="hidden" animate="show" className="block">
                  Before you buy the dream,
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1.5">
                <motion.span custom={1} variants={revealLine} initial="hidden" animate="show" className="block">
                  check the <span className="italic text-[#15803D]">lizard.</span>
                </motion.span>
              </span>
            </h1>
            <motion.div
              style={{ y: mascotY, rotate: mascotRotate }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.5, type: "spring", bounce: 0.45 }}
              className="hidden sm:block shrink-0 w-36 lg:w-44"
            >
              <Mascot variant="full" className="w-full drop-shadow-[0_18px_30px_rgba(20,25,21,0.22)]" />
            </motion.div>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-4 max-w-xl text-base text-[#525E54] leading-relaxed"
          >
            The important scoop on viral money-making offers — in about 30 seconds.
          </motion.p>
        </div>
      </section>

      <Marquee />

      <FeedTheLizard />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#15803D]" data-testid="trending-eyebrow">
            Trending Money-Making Offers
          </p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#141915]">
            The Top 10 right now
          </h2>
          <p className="mt-1.5 text-sm text-[#525E54]">
            The offers we're watching right now — researched so you don't have to waste your time.
          </p>
        </motion.div>

        <div className="flex flex-col gap-3.5" data-testid="top-ten-list">
          {scams.map((s, i) => (
            <ScamCard key={s.slug} scam={s} index={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-8 flex items-center gap-3 rounded-3xl border border-dashed border-[#BBE7C6] bg-[#EBF7EE] px-5 py-4"
        >
          <Mascot className="w-9 h-9 shrink-0" />
          <p className="font-display italic text-[#14532D]">"Don't research the scam. We already did."</p>
        </motion.div>

        <section className="mt-16 sm:mt-20" data-testid="how-it-works-section">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#15803D]">
              How ScamLizard Works
            </p>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#141915]">
              Three steps. Thirty seconds.
            </h2>
          </motion.div>
          <div className="mt-6 grid gap-3.5 sm:grid-cols-3">
            {steps.map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-3xl border border-[#E5DFD7] bg-white p-5"
                data-testid={`how-it-works-step-${i + 1}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-xl tracking-tighter text-[#141915]/15">{step.n}</span>
                  <step.icon className="w-5 h-5 text-[#15803D]" strokeWidth={2} />
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold text-[#141915]">{step.title}</h3>
                <p className="mt-1.5 text-sm text-[#525E54] leading-relaxed">{step.body}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-6 rounded-3xl bg-[#141915] px-6 py-7 sm:px-8"
            data-testid="independence-creed"
          >
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#22C55E]">
              The ScamLizard Principle
            </p>
            <p className="mt-3 font-display text-xl sm:text-2xl font-semibold text-white leading-snug">
              No paid rankings. No promoted money-making opportunities. No "try this instead."
            </p>
            <p className="mt-2.5 text-base text-white/90 font-medium leading-relaxed max-w-xl">
              ScamLizard exists to provide the scoop, not sell you another dream.
            </p>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
